import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Search, Plus, Edit2, Trash2, AlertTriangle, Clock, Package, Filter, Download, Shield, Upload, FileSpreadsheet, CheckCircle, XCircle, ScanLine } from 'lucide-react';
import { BarcodeScanner } from './BarcodeScanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyMedicine = {
  brand_name: '',
  salt_composition: '',
  hsn_code: '',
  batch_id: '',
  expiry_date: '',
  mrp: '',
  purchase_price: '',
  stock_quantity: '',
  min_stock_level: '10',
  gst_percentage: '12',
  manufacturer: '',
  category: 'General',
  schedule_type: 'none',
  barcode: ''
};

const categories = ['General', 'Analgesic', 'Antibiotic', 'Antacid', 'Antidiabetic', 'Cardiovascular', 'Antihistamine', 'Supplement', 'Psychotropic', 'Other'];
const scheduleTypes = [
  { value: 'none', label: 'None (OTC)' },
  { value: 'H1', label: 'Schedule H1' },
  { value: 'X', label: 'Schedule X' }
];

function Inventory() {
  const [medicines, setMedicines] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingMedicine, setEditingMedicine] = useState(null);
  const [formData, setFormData] = useState(emptyMedicine);
  const [loading, setLoading] = useState(false);
  const [inlineEdit, setInlineEdit] = useState({ id: null, field: null, value: '' });
  const [importData, setImportData] = useState([]);
  const [importPreview, setImportPreview] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const [showScanner, setShowScanner] = useState(false);
  const [scanTarget, setScanTarget] = useState('search'); // 'search' or 'form'

  const fetchMedicines = useCallback(async function() {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filter === 'low-stock') params.append('low_stock', 'true');
      if (filter === 'near-expiry') params.append('near_expiry', 'true');
      
      const response = await axios.get(`${API}/medicines?${params}`);
      setMedicines(response.data);
    } catch (error) {
      toast.error('Failed to fetch medicines');
    }
  }, [searchTerm, filter]);

  useEffect(function() {
    fetchMedicines();
  }, [fetchMedicines]);

  useEffect(function() {
    const params = new URLSearchParams(window.location.search);
    const urlFilter = params.get('filter');
    if (urlFilter) {
      setFilter(urlFilter);
    }
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        brand_name: formData.brand_name,
        salt_composition: formData.salt_composition,
        hsn_code: formData.hsn_code,
        mrp: parseFloat(formData.mrp),
        min_stock_level: parseInt(formData.min_stock_level),
        gst_percentage: parseFloat(formData.gst_percentage),
        manufacturer: formData.manufacturer,
        category: formData.category,
        schedule_type: formData.schedule_type || 'none',
        barcode: formData.barcode || null,
        batches: [{
          batch_id: formData.batch_id,
          quantity: parseInt(formData.stock_quantity),
          expiry_date: formData.expiry_date,
          purchase_price: parseFloat(formData.purchase_price),
          purchase_date: new Date().toISOString().split('T')[0]
        }]
      };

      if (editingMedicine) {
        // For updates, only send the fields that need updating (not batches structure)
        const updateData = {
          brand_name: formData.brand_name,
          salt_composition: formData.salt_composition,
          hsn_code: formData.hsn_code,
          mrp: parseFloat(formData.mrp),
          min_stock_level: parseInt(formData.min_stock_level),
          gst_percentage: parseFloat(formData.gst_percentage),
          manufacturer: formData.manufacturer,
          category: formData.category,
          schedule_type: formData.schedule_type || 'none',
          barcode: formData.barcode || null
        };
        await axios.put(`${API}/medicines/${editingMedicine.id}`, updateData);
        toast.success('Medicine updated successfully');
      } else {
        await axios.post(`${API}/medicines`, data);
        toast.success('Medicine added successfully');
      }
      
      setShowAddModal(false);
      setEditingMedicine(null);
      setFormData(emptyMedicine);
      fetchMedicines();
    } catch (error) {
      const msg = error.response && error.response.data && error.response.data.detail;
      toast.error(msg || 'Operation failed');
    } finally {
      setLoading(false);
    }
  }

  function handleEdit(medicine) {
    setEditingMedicine(medicine);
    const firstBatch = medicine.batches && medicine.batches.length > 0 ? medicine.batches[0] : null;
    setFormData({
      brand_name: medicine.brand_name,
      salt_composition: medicine.salt_composition,
      hsn_code: medicine.hsn_code,
      batch_id: firstBatch ? firstBatch.batch_id : '',
      expiry_date: firstBatch ? firstBatch.expiry_date : '',
      mrp: medicine.mrp.toString(),
      purchase_price: firstBatch ? firstBatch.purchase_price.toString() : '0',
      stock_quantity: medicine.total_stock.toString(),
      min_stock_level: medicine.min_stock_level.toString(),
      gst_percentage: medicine.gst_percentage.toString(),
      manufacturer: medicine.manufacturer,
      category: medicine.category,
      schedule_type: medicine.schedule_type || 'none',
      barcode: medicine.barcode || ''
    });
    setShowAddModal(true);
  }

  async function handleDelete(id) {
    if (!window.confirm('Are you sure you want to delete this medicine?')) return;
    try {
      await axios.delete(`${API}/medicines/${id}`);
      toast.success('Medicine deleted');
      fetchMedicines();
    } catch (error) {
      toast.error('Failed to delete medicine');
    }
  }

  function handleInlineEditStart(id, field, value) {
    setInlineEdit({ id: id, field: field, value: value.toString() });
  }

  async function handleInlineEditSave() {
    if (!inlineEdit.id) return;
    try {
      let value;
      if (inlineEdit.field === 'stock_quantity' || inlineEdit.field === 'min_stock_level') {
        value = parseInt(inlineEdit.value);
      } else {
        value = parseFloat(inlineEdit.value);
      }
      
      const updateData = {};
      updateData[inlineEdit.field] = value;
      
      await axios.put(`${API}/medicines/${inlineEdit.id}`, updateData);
      toast.success('Updated');
      fetchMedicines();
    } catch (error) {
      toast.error('Update failed');
    } finally {
      setInlineEdit({ id: null, field: null, value: '' });
    }
  }

  async function handleSeedData() {
    try {
      await axios.post(`${API}/seed-demo-data`);
      toast.success('Demo data loaded');
      fetchMedicines();
    } catch (error) {
      toast.error('Failed to load demo data');
    }
  }

  function handleFileSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = function(event) {
      const text = event.target.result;
      parseCSV(text);
    };
    reader.readAsText(file);
  }

  function parseCSV(text) {
    const lines = text.split('\n').filter(function(line) { return line.trim(); });
    if (lines.length < 2) {
      toast.error('CSV file must have at least a header and one data row');
      return;
    }
    
    const headers = lines[0].split(',').map(function(h) { return h.trim().toLowerCase().replace(/"/g, ''); });
    const data = [];
    const preview = [];
    
    // Map column indices
    const colMap = {
      brand_name: headers.indexOf('brand_name'),
      salt_composition: headers.indexOf('salt_composition'),
      hsn_code: headers.indexOf('hsn_code'),
      mrp: headers.indexOf('mrp'),
      gst_percentage: headers.indexOf('gst_percentage'),
      manufacturer: headers.indexOf('manufacturer'),
      category: headers.indexOf('category'),
      batch_id: headers.indexOf('batch_id'),
      quantity: headers.indexOf('quantity'),
      expiry_date: headers.indexOf('expiry_date'),
      purchase_price: headers.indexOf('purchase_price')
    };
    
    // Check required columns
    if (colMap.brand_name === -1 || colMap.batch_id === -1 || colMap.quantity === -1) {
      toast.error('CSV must have brand_name, batch_id, and quantity columns');
      return;
    }
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(function(v) { return v.trim().replace(/"/g, ''); });
      
      const item = {
        brand_name: values[colMap.brand_name] || '',
        salt_composition: colMap.salt_composition >= 0 ? values[colMap.salt_composition] || '' : '',
        hsn_code: colMap.hsn_code >= 0 ? values[colMap.hsn_code] || '' : '',
        mrp: colMap.mrp >= 0 ? parseFloat(values[colMap.mrp]) || 0 : 0,
        gst_percentage: colMap.gst_percentage >= 0 ? parseFloat(values[colMap.gst_percentage]) || 12 : 12,
        manufacturer: colMap.manufacturer >= 0 ? values[colMap.manufacturer] || '' : '',
        category: colMap.category >= 0 ? values[colMap.category] || 'General' : 'General',
        batch_id: values[colMap.batch_id] || '',
        quantity: parseInt(values[colMap.quantity]) || 0,
        expiry_date: colMap.expiry_date >= 0 ? values[colMap.expiry_date] || '' : '',
        purchase_price: colMap.purchase_price >= 0 ? parseFloat(values[colMap.purchase_price]) || 0 : 0
      };
      
      if (item.brand_name && item.batch_id && item.quantity > 0) {
        data.push(item);
        preview.push({
          ...item,
          valid: !!(item.brand_name && item.batch_id && item.quantity > 0 && item.expiry_date)
        });
      }
    }
    
    setImportData(data);
    setImportPreview(preview);
    setImportResult(null);
  }

  async function handleBulkImport() {
    if (importData.length === 0) {
      toast.error('No valid data to import');
      return;
    }
    
    setImportLoading(true);
    try {
      const res = await axios.post(`${API}/inventory/bulk-import`, importData);
      setImportResult(res.data);
      toast.success(`Imported: ${res.data.created} created, ${res.data.updated} updated`);
      fetchMedicines();
    } catch (error) {
      const msg = error.response && error.response.data && error.response.data.detail;
      toast.error(msg || 'Import failed');
    } finally {
      setImportLoading(false);
    }
  }

  async function downloadTemplate() {
    try {
      const response = await axios.get(`${API}/inventory/import-template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'medicine_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download template');
    }
  }

  function resetImport() {
    setImportData([]);
    setImportPreview([]);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  function handleModalClose(open) {
    setShowAddModal(open);
    if (!open) {
      setEditingMedicine(null);
      setFormData(emptyMedicine);
    }
  }

  async function handleBarcodeScan(scannedCode) {
    if (scanTarget === 'form') {
      // Scanning for the barcode field in the add/edit form
      updateFormField('barcode', scannedCode);
      toast.success('Barcode scanned: ' + scannedCode);
      return;
    }
    // Scanning for stock lookup
    try {
      const res = await axios.get(`${API}/medicines/barcode/${encodeURIComponent(scannedCode)}`);
      if (res.data.found) {
        const med = res.data.medicine;
        toast.success('Found: ' + med.brand_name + ' (by ' + res.data.match_type + ')');
        handleEdit(med);
      } else {
        toast.info('No match found. Opening new medicine form with barcode.');
        setFormData({ ...emptyMedicine, barcode: scannedCode });
        setShowAddModal(true);
      }
    } catch (e) {
      toast.error('Scan lookup failed');
      setSearchTerm(scannedCode);
    }
  }

  function updateFormField(field, value) {
    const updated = { ...formData };
    updated[field] = value;
    setFormData(updated);
  }

  function renderCategoryOptions() {
    const opts = [];
    for (let i = 0; i < categories.length; i++) {
      opts.push(<SelectItem key={categories[i]} value={categories[i]}>{categories[i]}</SelectItem>);
    }
    return opts;
  }

  function renderScheduleOptions() {
    const opts = [];
    for (let i = 0; i < scheduleTypes.length; i++) {
      opts.push(<SelectItem key={scheduleTypes[i].value} value={scheduleTypes[i].value}>{scheduleTypes[i].label}</SelectItem>);
    }
    return opts;
  }

  function renderTableRows() {
    if (medicines.length === 0) {
      return (
        <tr>
          <td colSpan="7" className="text-center py-8 text-slate-400">
            <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No medicines found. Add your first medicine or load demo data.</p>
          </td>
        </tr>
      );
    }

    const rows = [];
    for (let i = 0; i < medicines.length; i++) {
      const medicine = medicines[i];
      const isScheduled = medicine.schedule_type && medicine.schedule_type !== 'none';
      // Get first batch info for display (or use totals)
      const firstBatch = medicine.batches && medicine.batches.length > 0 ? medicine.batches[0] : null;
      const batchId = firstBatch ? firstBatch.batch_id : 'N/A';
      const expiryDate = medicine.nearest_expiry || (firstBatch ? firstBatch.expiry_date : 'N/A');
      
      rows.push(
        <tr key={medicine.id} data-testid={`medicine-row-${medicine.id}`}>
          <td>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium text-navy-900">{medicine.brand_name}</p>
                {isScheduled && (
                  <Badge className="text-xs bg-red-100 text-red-800 border-red-300">
                    <Shield className="w-3 h-3 mr-1" />
                    {medicine.schedule_type}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-500">{medicine.salt_composition}</p>
              <Badge variant="outline" className="text-xs mt-1">{medicine.category}</Badge>
            </div>
          </td>
          <td>
            <p className="font-mono text-sm">{batchId}</p>
            <p className="text-xs text-slate-500 font-mono">{medicine.hsn_code}</p>
          </td>
          <td>
            <p className={`font-mono text-sm ${medicine.is_near_expiry ? 'text-red-600 font-medium' : ''}`}>
              {expiryDate}
            </p>
          </td>
          <td>
            <span className={`font-mono ${medicine.is_low_stock ? 'text-amber-600 font-medium' : ''}`}>
              {medicine.total_stock}
            </span>
          </td>
          <td className="font-mono">₹{medicine.mrp}</td>
          <td>
            <div className="flex flex-wrap gap-1">
              {medicine.is_low_stock && (
                <Badge className="badge-low-stock text-xs">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Low
                </Badge>
              )}
              {medicine.is_near_expiry && (
                <Badge className="badge-near-expiry text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  Expiry
                </Badge>
              )}
              {!medicine.is_low_stock && !medicine.is_near_expiry && (
                <Badge className="badge-ok text-xs">OK</Badge>
              )}
            </div>
          </td>
          <td className="text-right">
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={function() { handleEdit(medicine); }}
                data-testid={`edit-${medicine.id}`}
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-red-500 hover:text-red-700"
                onClick={function() { handleDelete(medicine.id); }}
                data-testid={`delete-${medicine.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </td>
        </tr>
      );
    }
    return rows;
  }

  return (
    <div className="p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy-900">Inventory Management</h1>
          <p className="text-slate-500 text-sm">{medicines.length} medicines in stock</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={handleSeedData} data-testid="seed-data-btn">
            <Download className="w-4 h-4 mr-2" />
            Load Demo Data
          </Button>
          <Dialog open={showImportModal} onOpenChange={function(open) { setShowImportModal(open); if (!open) resetImport(); }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100" data-testid="bulk-import-btn">
                <Upload className="w-4 h-4 mr-2" />
                Bulk Import
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                  Bulk Import Medicines (CSV)
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Instructions */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800 mb-2">
                    Upload a CSV file with your medicine stock data. Required columns: <strong>brand_name, batch_id, quantity</strong>
                  </p>
                  <Button variant="outline" size="sm" onClick={downloadTemplate}>
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>
                </div>
                
                {/* File Upload */}
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    ref={fileInputRef}
                    id="csv-upload"
                  />
                  <label htmlFor="csv-upload" className="cursor-pointer">
                    <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                    <p className="text-slate-600">Click to upload CSV file</p>
                    <p className="text-xs text-slate-400">or drag and drop</p>
                  </label>
                </div>
                
                {/* Preview */}
                {importPreview.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-navy-900">Preview ({importPreview.length} items)</h4>
                      <Button variant="ghost" size="sm" onClick={resetImport}>Clear</Button>
                    </div>
                    <ScrollArea className="h-48 border rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="p-2 text-left">Valid</th>
                            <th className="p-2 text-left">Brand Name</th>
                            <th className="p-2 text-left">Batch</th>
                            <th className="p-2 text-right">Qty</th>
                            <th className="p-2 text-left">Expiry</th>
                            <th className="p-2 text-right">MRP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importPreview.map(function(item, idx) {
                            return (
                              <tr key={idx} className={item.valid ? '' : 'bg-red-50'}>
                                <td className="p-2">
                                  {item.valid ? (
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  ) : (
                                    <XCircle className="w-4 h-4 text-red-500" />
                                  )}
                                </td>
                                <td className="p-2 font-medium">{item.brand_name}</td>
                                <td className="p-2 font-mono text-xs">{item.batch_id}</td>
                                <td className="p-2 text-right">{item.quantity}</td>
                                <td className="p-2 font-mono text-xs">{item.expiry_date || 'Missing'}</td>
                                <td className="p-2 text-right">₹{item.mrp}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </ScrollArea>
                  </div>
                )}
                
                {/* Import Result */}
                {importResult && (
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">Import Complete</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-green-700">Created</p>
                        <p className="text-2xl font-bold text-green-800">{importResult.created}</p>
                      </div>
                      <div>
                        <p className="text-blue-700">Updated</p>
                        <p className="text-2xl font-bold text-blue-800">{importResult.updated}</p>
                      </div>
                      <div>
                        <p className="text-red-700">Errors</p>
                        <p className="text-2xl font-bold text-red-800">{importResult.errors.length}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Actions */}
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={function() { setShowImportModal(false); resetImport(); }}>
                    Close
                  </Button>
                  <Button 
                    className="bg-mint-600 hover:bg-mint-700" 
                    onClick={handleBulkImport}
                    disabled={importData.length === 0 || importLoading}
                  >
                    {importLoading ? 'Importing...' : `Import ${importData.length} Items`}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={showAddModal} onOpenChange={handleModalClose}>
            <DialogTrigger asChild>
              <Button className="bg-mint-600 hover:bg-mint-700" data-testid="add-medicine-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Medicine
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingMedicine ? 'Edit Medicine' : 'Add New Medicine'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="brand_name">Brand Name *</Label>
                    <Input
                      id="brand_name"
                      data-testid="medicine-brand-name"
                      value={formData.brand_name}
                      onChange={function(e) { updateFormField('brand_name', e.target.value); }}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="salt_composition">Salt Composition *</Label>
                    <Input
                      id="salt_composition"
                      data-testid="medicine-salt"
                      value={formData.salt_composition}
                      onChange={function(e) { updateFormField('salt_composition', e.target.value); }}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hsn_code">HSN Code *</Label>
                    <Input
                      id="hsn_code"
                      data-testid="medicine-hsn"
                      value={formData.hsn_code}
                      onChange={function(e) { updateFormField('hsn_code', e.target.value); }}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="batch_id">Batch ID *</Label>
                    <Input
                      id="batch_id"
                      data-testid="medicine-batch"
                      value={formData.batch_id}
                      onChange={function(e) { updateFormField('batch_id', e.target.value); }}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="expiry_date">Expiry Date *</Label>
                    <Input
                      id="expiry_date"
                      type="date"
                      data-testid="medicine-expiry"
                      value={formData.expiry_date}
                      onChange={function(e) { updateFormField('expiry_date', e.target.value); }}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Select value={formData.category} onValueChange={function(v) { updateFormField('category', v); }}>
                      <SelectTrigger data-testid="medicine-category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {renderCategoryOptions()}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mrp">MRP (₹) *</Label>
                    <Input
                      id="mrp"
                      type="number"
                      step="0.01"
                      data-testid="medicine-mrp"
                      value={formData.mrp}
                      onChange={function(e) { updateFormField('mrp', e.target.value); }}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="purchase_price">Purchase Price (₹) *</Label>
                    <Input
                      id="purchase_price"
                      type="number"
                      step="0.01"
                      data-testid="medicine-purchase-price"
                      value={formData.purchase_price}
                      onChange={function(e) { updateFormField('purchase_price', e.target.value); }}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stock_quantity">Stock Quantity *</Label>
                    <Input
                      id="stock_quantity"
                      type="number"
                      data-testid="medicine-stock"
                      value={formData.stock_quantity}
                      onChange={function(e) { updateFormField('stock_quantity', e.target.value); }}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="min_stock_level">Min Stock Level</Label>
                    <Input
                      id="min_stock_level"
                      type="number"
                      data-testid="medicine-min-stock"
                      value={formData.min_stock_level}
                      onChange={function(e) { updateFormField('min_stock_level', e.target.value); }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gst_percentage">GST %</Label>
                    <Input
                      id="gst_percentage"
                      type="number"
                      step="0.1"
                      data-testid="medicine-gst"
                      value={formData.gst_percentage}
                      onChange={function(e) { updateFormField('gst_percentage', e.target.value); }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="manufacturer">Manufacturer</Label>
                    <Input
                      id="manufacturer"
                      data-testid="medicine-manufacturer"
                      value={formData.manufacturer}
                      onChange={function(e) { updateFormField('manufacturer', e.target.value); }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="barcode">Barcode (EAN/GTIN)</Label>
                    <div className="flex gap-2">
                      <Input
                        id="barcode"
                        data-testid="medicine-barcode"
                        placeholder="Scan or enter barcode"
                        value={formData.barcode}
                        onChange={function(e) { updateFormField('barcode', e.target.value); }}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="shrink-0 border-mint-300 text-mint-700 hover:bg-mint-50"
                        onClick={function() { setScanTarget('form'); setShowScanner(true); }}
                        data-testid="form-scan-barcode-btn"
                      >
                        <ScanLine className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="schedule_type">Schedule Type</Label>
                    <Select value={formData.schedule_type} onValueChange={function(v) { updateFormField('schedule_type', v); }}>
                      <SelectTrigger data-testid="medicine-schedule">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {renderScheduleOptions()}
                      </SelectContent>
                    </Select>
                    {formData.schedule_type !== 'none' && (
                      <p className="text-xs text-red-600 mt-1">
                        <Shield className="w-3 h-3 inline mr-1" />
                        Requires prescriber details at billing
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={function() { setShowAddModal(false); }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-mint-600 hover:bg-mint-700" disabled={loading}>
                    {loading ? 'Saving...' : (editingMedicine ? 'Update' : 'Add Medicine')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="inventory-search"
                placeholder="Search by name, salt, batch, or barcode..."
                className="pl-10"
                value={searchTerm}
                onChange={function(e) { setSearchTerm(e.target.value); }}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="border-mint-300 text-mint-700 hover:bg-mint-50"
                onClick={function() { setScanTarget('search'); setShowScanner(true); }}
                data-testid="inventory-scan-btn"
              >
                <ScanLine className="w-4 h-4 mr-1" />Scan
              </Button>
              <Filter className="w-4 h-4 text-slate-400" />
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-40" data-testid="inventory-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Items</SelectItem>
                  <SelectItem value="low-stock">Low Stock</SelectItem>
                  <SelectItem value="near-expiry">Near Expiry</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Inventory Table */}
      <Card>
        <CardContent className="p-0">
          <ScrollArea className="h-[calc(100vh-320px)]">
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>Medicine</th>
                  <th>Batch / HSN</th>
                  <th>Expiry</th>
                  <th>Stock</th>
                  <th>MRP</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {renderTableRows()}
              </tbody>
            </table>
          </ScrollArea>
        </CardContent>
      </Card>

      <BarcodeScanner
        open={showScanner}
        onOpenChange={setShowScanner}
        onScanResult={handleBarcodeScan}
      />
    </div>
  );
}

export default Inventory;
